import React, { useState } from "react";
import api from "../../setupAxios";
import { useDialog } from "../../context/dialogContext";
import { descargar, msgError, useListado } from "../../utils";

const TIPOS = { alumno: "Alumno", admisiones: "Admisiones", ensombrado: "Ensombrado", checkin: "Check-in", admin: "Admin" };
const TIPOS_ALTA = ["admisiones", "ensombrado", "checkin", "admin"];

// Alta y edición. Alumnos: solo correo y clave (el nombre y el piso vienen de ch_base).
// Resto: nombre, usuario, correo (opcional), tipo y clave. Clave vacía al editar = no se cambia.
function FormUsuario({ usuario, cerrar, alGuardar }) {
  const { avisar, exito } = useDialog();
  const nuevo = !usuario;
  const esAlumno = usuario?.tipo === "alumno";
  const [f, setF] = useState({
    nombre: usuario?.nombre || "", username: usuario?.username || "", email: usuario?.email || "",
    tipo: usuario?.tipo || "admisiones", password: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const sinAcceso = !esAlumno && !f.username.trim() && !f.email.trim();

  const guardar = async (regenerar = false) => {
    try {
      if (nuevo) {
        const { data } = await api.post("/admin/usuarios", {
          tipo: f.tipo, nombre: f.nombre, username: f.username, email: f.email, password: f.password,
        });
        exito(`Usuario creado: ${data.username || f.email} · clave ${data.password}`, 12000);
      } else {
        const cuerpo = {};
        if (!esAlumno) {
          if (f.nombre !== usuario.nombre) cuerpo.nombre = f.nombre;
          if (f.username !== (usuario.username || "")) cuerpo.username = f.username;
          if (f.tipo !== usuario.tipo) cuerpo.tipo = f.tipo;
        }
        if (f.email !== (usuario.email || "")) cuerpo.email = f.email;
        if (regenerar) cuerpo.regenerar_password = true;
        else if (f.password.trim()) cuerpo.password = f.password.trim();
        if (Object.keys(cuerpo).length === 0) { avisar("No has cambiado nada."); return; }
        const { data } = await api.put(`/admin/usuarios/${usuario.id}`, cuerpo);
        exito(data.password ? `Guardado · clave nueva: ${data.password}` : "Usuario actualizado", data.password ? 12000 : 4500);
      }
      alGuardar();
    } catch (e) { avisar(msgError(e)); }
  };

  return (
    <div className="modal-back" onMouseDown={cerrar}>
      <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
        <h3>{nuevo ? "➕ Nuevo usuario" : `✏️ ${esAlumno ? "Alumno" : "Usuario"}: ${usuario.nombre}`}</h3>
        {esAlumno && <p className="muted">El nombre y el piso de un alumno vienen de ch_base. Aquí puedes cambiar su correo y su clave.</p>}
        {nuevo && (
          <div className="field"><label>Tipo</label>
            <select value={f.tipo} onChange={set("tipo")}>{TIPOS_ALTA.map((t) => <option key={t} value={t}>{TIPOS[t]}</option>)}</select></div>
        )}
        {!nuevo && !esAlumno && (
          <div className="field"><label>Tipo</label>
            <select value={f.tipo} onChange={set("tipo")}>{TIPOS_ALTA.map((t) => <option key={t} value={t}>{TIPOS[t]}</option>)}</select></div>
        )}
        {!esAlumno && <div className="field"><label>Nombre</label><input value={f.nombre} onChange={set("nombre")} maxLength={150} /></div>}
        {!esAlumno && (
          <div className="field"><label>Usuario (con él entra; minúsculas, números, . _ -)</label>
            <input value={f.username} onChange={set("username")} maxLength={40} autoCapitalize="none" spellCheck={false} placeholder="Ej.: maria.admisiones" /></div>
        )}
        <div className="field"><label>Correo {esAlumno ? "(con él entra el alumno)" : "(opcional si tiene usuario)"}</label>
          <input type="email" value={f.email} onChange={set("email")} /></div>
        <div className="field">
          <label>{nuevo ? "Clave (vacío = se genera una normal)" : "Clave nueva (vacío = no se cambia)"}</label>
          <input value={f.password} onChange={set("password")} maxLength={64} autoComplete="off" placeholder="Entre 6 y 64 caracteres, sin espacios" />
        </div>
        {sinAcceso && <div className="alert alert-error">Pon al menos un usuario o un correo, o no podrá entrar.</div>}
        <div className="modal-actions">
          <button className="btn" onClick={cerrar}>Cancelar</button>
          {!nuevo && <button className="btn" onClick={() => guardar(true)} title="Genera una clave nueva y la guarda">🔑 Generar clave</button>}
          <button className="btn btn-primary" disabled={(!esAlumno && !f.nombre.trim()) || sinAcceso || (esAlumno && !f.email.trim())}
                  onClick={() => guardar(false)}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

export default function TabUsuarios() {
  const { avisar, confirmar, exito } = useDialog();
  const [f, setF] = useState({ tipo: "", q: "", activo: "" });
  const [form, setForm] = useState(null);       // null | "nuevo" | usuario
  const [ocupado, setOcupado] = useState(false);
  const { datos, cargando, error, recargar } = useListado("/admin/usuarios", f, "usuarios");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const sincronizar = async () => {
    if (!(await confirmar("Se crearán los alumnos de ch_base que aún no tengan usuario (con clave de 8 dígitos), se actualizarán nombres y correos (salvo los correos que hayas cambiado a mano) y se desactivarán los dados de baja. ¿Continuar?"))) return;
    setOcupado(true);
    try {
      const { data } = await api.post("/admin/usuarios/sincronizar");
      await avisar(`Sincronizado:\n• ${data.creados} alumnos nuevos\n• ${data.actualizados} actualizados\n• ${data.desactivados} desactivados por baja\n• ${data.sin_correo} sin correo válido (no se pueden crear)\n• ${data.duplicados.length} con el correo repetido${data.duplicados.length ? " (revisar en ch_base)" : ""}`);
      recargar();
    } catch (e) { avisar(msgError(e)); }
    setOcupado(false);
  };
  const csv = async () => {
    const err = await descargar("/admin/usuarios/export.csv", { tipo: f.tipo || undefined }, "usuarios_check_in.csv");
    if (err) avisar(err);
  };
  const alternar = async (u) => {
    try { await api.put(`/admin/usuarios/${u.id}`, { activo: !u.activo }); recargar(); } catch (e) { avisar(msgError(e)); }
  };
  const regenerar = async (u) => {
    if (!(await confirmar(`¿Generar una clave nueva para ${u.nombre}? La anterior dejará de valer.`))) return;
    try { const { data } = await api.put(`/admin/usuarios/${u.id}`, { regenerar_password: true }); exito(`Nueva clave de ${u.nombre}: ${data.password}`, 10000); recargar(); }
    catch (e) { avisar(msgError(e)); }
  };
  const copiar = async (txt) => { try { await navigator.clipboard.writeText(txt); exito("Copiada"); } catch { avisar(txt); } };

  return (
    <div className="card">
      <div className="card-headrow">
        <h3>👥 Usuarios <span className="count">{datos.length}</span></h3>
        <div className="card-headrow-acc">
          <button className="btn" onClick={csv}>⬇️ CSV para Mailchimp{f.tipo ? ` (${TIPOS[f.tipo]})` : " (todos)"}</button>
          <button className="btn" disabled={ocupado} onClick={sincronizar}>{ocupado ? "Sincronizando…" : "🔄 Sincronizar alumnos desde ch_base"}</button>
          <button className="btn btn-primary" onClick={() => setForm("nuevo")}>➕ Nuevo usuario</button>
        </div>
      </div>
      <p className="muted">Los alumnos entran con su <b>correo</b>; el resto con su <b>usuario</b> (o correo). Aquí puedes cambiar usuario, correo y clave de cualquiera.</p>
      <div className="ci-filtros">
        <div className="field ancho"><label>Buscar (nombre, usuario o correo)</label><input value={f.q} onChange={set("q")} /></div>
        <div className="field"><label>Tipo</label>
          <select value={f.tipo} onChange={set("tipo")}><option value="">Todos</option>{Object.entries(TIPOS).map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></div>
        <div className="field"><label>Estado</label>
          <select value={f.activo} onChange={set("activo")}><option value="">Todos</option><option value="1">Activos</option><option value="0">Inactivos</option></select></div>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="tablewrap">
        <table className="ci-tabla t-usuarios">
          <thead><tr><th>Nombre</th><th>Usuario</th><th>Correo</th><th>Tipo</th><th>Clave</th><th>Estado</th><th /></tr></thead>
          <tbody>
            {cargando && datos.length === 0 && <tr><td colSpan={7} className="empty">Cargando…</td></tr>}
            {!cargando && datos.length === 0 && <tr><td colSpan={7} className="empty">No hay usuarios con esos filtros.</td></tr>}
            {datos.map((u) => (
              <tr key={u.id}>
                <td className="ci-nombre">{u.nombre}</td>
                <td className="strong">{u.username || <span className="muted">—</span>}</td>
                <td>{u.email || <span className="muted">—</span>}</td>
                <td><span className="badge badge-blue">{TIPOS[u.tipo]}</span></td>
                <td><span className="ci-clave">{u.password}</span> <button className="btn btn-mini" onClick={() => copiar(u.password)} title="Copiar clave">📋</button></td>
                <td><button className={`btn btn-mini ${u.activo ? "btn-success" : ""}`} onClick={() => alternar(u)}>{u.activo ? "Activo" : "Inactivo"}</button></td>
                <td><div className="acc-cell">
                  <button className="btn btn-mini" onClick={() => regenerar(u)} title="Generar clave nueva">🔑</button>
                  <button className="btn btn-mini" onClick={() => setForm(u)} title="Editar usuario, correo y clave">✏️</button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {form && <FormUsuario usuario={form === "nuevo" ? null : form} cerrar={() => setForm(null)} alGuardar={() => { setForm(null); recargar(); }} />}
    </div>
  );
}
